/**
 * queueStore — LAYER 1 of the offline queue: persistence + listeners + reads.
 *
 * The queue was one 297-LOC file against a 300-LOC hard gate, so the cure for
 * BUG-2026-07-14-VC559 could not add a line to it. Split by COHESION, not to beat
 * a number, and strictly ONE-WAY so `check:circular` (madge) stays green:
 *
 *   queueStore (this file: what is in the queue)
 *      ↑ queueFlush   (how it drains)
 *      ↑ queueExit    (how a rep gets an entry OUT)
 *      ↑ offlineQueue (the facade every consumer keeps importing)
 *
 * Nothing here imports from any of those. The dependency arrow only points down.
 *
 * READS ONLY. Every write goes through queueStoreWrite — the serialised read-modify-write
 * chain (TASK-FE-OUTBOX-SERIALIZE-QUEUE-WRITES-2026-10-09) that sits directly above this
 * file. A writer here would be a door around that chain.
 */

import { get } from 'idb-keyval'
import type { QueueEntry, ConflictEvent, FlushResult, QueueFailureReason } from './offlineQueueTypes'
import type { QueueAck } from './ackVerify'
import { pinnedTwinKeyOf } from './queueTypes'

export const QUEUE_KEY = 'offline:writeQueue'
export const MAX_RETRIES_DEFAULT = 5

// CRITICAL-BY-DEFAULT. Every queued write is durable — exempt from the 72h expiry
// AND the dead-letter purge, retried until synced, a failed one kept + surfaced.
// Silent loss drove BUG-RI-APRIL-VISIT-CAPTURE-GAP + the SAE72 appointment loss,
// so the default is inverted (operator 2026-06-08): critical-unless-ephemeral.
// Only fire-and-forget telemetry would be ephemeral — nothing is, today.
//
// CONSEQUENCE, and it is load-bearing: because this set is EMPTY, `isCritical` is
// always true, so the dead-letter purge can never fire. Nothing is ever removed
// automatically. That is exactly why every terminal state MUST have a USER-reachable
// exit (queueExit.ts) — otherwise an entry is immortal, which is what it was.
const EPHEMERAL_QUEUE_TYPES = new Set<string>([])

export function isCritical(type: string): boolean {
  return !EPHEMERAL_QUEUE_TYPES.has(type)
}

const handlers: Map<string, (payload: unknown) => Promise<QueueAck | void>> = new Map()
let changeListeners: Array<(count: number) => void> = []
let conflictListeners: Array<(event: ConflictEvent) => void> = []
let flushResultListeners: Array<(result: FlushResult) => void> = []

export async function readQueue(): Promise<QueueEntry[]> {
  const queue = await get<QueueEntry[]>(QUEUE_KEY)
  return queue ?? []
}

/** Fan the pending count out to onQueueChange subscribers — called by queueStoreWrite after each write. */
export function emitQueueChange(pendingCount: number): void {
  for (const listener of changeListeners) listener(pendingCount)
}

/** One UNLANDED predecessor entry the rep discarded: its queue type and its payload key. */
export interface DiscardedPredecessor { type: string; key: string }

export const DISCARDED_PREDECESSORS_KEY ='offline:discardedPredecessors'

/**
 * The predecessors that left the queue by DISCARD before they landed (queueOrdering's fourth
 * fate). The queue itself cannot say this: a discarded entry and one that synced are both
 * simply absent. Nothing stored yet = none discarded (empty IS the intent, not a masked read).
 */
export async function readDiscardedPredecessors(): Promise<DiscardedPredecessor[]> {
  const stored = await get<DiscardedPredecessor[]>(DISCARDED_PREDECESSORS_KEY)
  return stored === undefined ? [] : stored
}

/** Register a handler for a queue entry type. */
export function registerQueueHandler(type: string, handler: (payload: unknown) => Promise<QueueAck | void>): void {
  handlers.set(type, handler)
}

export function getHandler(type: string): ((payload: unknown) => Promise<QueueAck | void>) | undefined {
  return handlers.get(type)
}

/** Register `listener` on a listener array; returns an unsubscribe fn. */
function subscribe<T>(arr: T[], listener: T, replace: (next: T[]) => void): () => void {
  arr.push(listener)
  return () => replace(arr.filter(l => l !== listener))
}

/** Subscribe to pending-count changes. Returns an unsubscribe fn. */
export function onQueueChange(listener: (pendingCount: number) => void): () => void {
  return subscribe(changeListeners, listener, next => { changeListeners = next })
}

/** Subscribe to conflict events. Returns an unsubscribe fn. */
export function onConflict(listener: (event: ConflictEvent) => void): () => void {
  return subscribe(conflictListeners, listener, next => { conflictListeners = next })
}

/**
 * Subscribe to flush-cycle results. Returns an unsubscribe fn.
 *
 * D-33 R1 — the `online`-event + interval auto-flushes used to fire-and-forget the
 * `flushQueue()` promise, discarding its result, so a failed sync surfaced NOTHING.
 * Every flush result now reaches the failed-count banner.
 */
export function onFlushResult(listener: (result: FlushResult) => void): () => void {
  return subscribe(flushResultListeners, listener, next => { flushResultListeners = next })
}

export function emitConflict(event: ConflictEvent): void {
  for (const listener of conflictListeners) listener(event)
}

export function emitFlushResult(result: FlushResult): void {
  for (const listener of flushResultListeners) listener(result)
}

/** Count of pending entries. */
export async function getPendingCount(): Promise<number> {
  const queue = await readQueue()
  return queue.filter(e => e.status === 'pending').length
}

/** All queue entries (for debug/UI/projections). */
export async function getQueueEntries(): Promise<QueueEntry[]> {
  return readQueue()
}

/** The write a delivered attempt resolves: its id (never itself a twin), door and body. */
export type DeliveredWrite = Pick<QueueEntry, 'id' | 'type' | 'payload'>

/**
 * The OLDER PINNED copies of the write `delivered` just DELIVERED: entries QUEUED BEFORE it (queue order
 * is enqueue order) of the same door with the same pinned-twin key (queueTypes.pinnedTwinKeyOf — only the
 * PIN_ON_FINAL doors have one), genuinely failed. Pending / conflict entries, every other door's pins and
 * a pin made AFTER it (a later body, refused for its own content) are never twins. Null when `delivered`
 * is no longer in `queue`: a flush that raced the online attempt delivered and pruned it, and resolved its
 * twins itself (settleDeliveredTwins) — nothing is left to order them against.
 * Pure; the online submit's removal (queueStoreWrite.removeDeliveredTwins) asks it inside the write chain.
 */
export function olderPinnedTwins(queue: readonly QueueEntry[], delivered: DeliveredWrite): QueueEntry[] | null {
  const at = queue.findIndex(e => e.id === delivered.id)
  if (at < 0) return null
  const key = pinnedTwinKeyOf(delivered.type, delivered.payload)
  if (key === null) return []
  return queue.slice(0, at).filter(e => e.type === delivered.type && isGenuinelyFailed(e) && pinnedTwinKeyOf(e.type, e.payload) === key)
}

/**
 * The drain path's twin of queueStoreWrite.removeDeliveredTwins, IN PLACE on a flush cycle's SNAPSHOT:
 * each pinned twin of the just-delivered `delivered` takes the synced-sentinel shape (failed, retries
 * below max), so the rest of THIS cycle reads it as LANDED (a v8 behind that key is not judged against
 * a dead copy of a write that just landed) and the cycle-end prune removes it. Returns the twins it
 * settled: the flush records them among the entries it CHANGED, so their sentinel shape is applied by
 * id onto the stored queue (a snapshot edit alone is never written back).
 * THROWS for a twin whose maxRetries is below 1: it cannot take the sentinel shape, and leaving it
 * pinned would show a delivered report as "nicht gesendet".
 */
export function settleDeliveredTwins(queue: QueueEntry[], delivered: DeliveredWrite): QueueEntry[] {
  const twins = olderPinnedTwins(queue, delivered)
  if (twins === null) throw new Error(`[offlineQueue] delivered entry ${delivered.id} is not in the flush cycle's queue`)
  for (const twin of twins) {
    if (twin.maxRetries < 1) throw new Error(`[offlineQueue] delivered twin ${twin.id} cannot be marked landed (maxRetries ${twin.maxRetries})`)
    twin.retries = 0
    delete twin.failureReason
    delete twin.finalCode
  }
  return twins
}

/**
 * Genuinely-failed entries (exhausted retries) needing attention — NOT silently
 * dropped: surfaced loudly by the OfflineIndicator. (`failed` + `retries < maxRetries`
 * is the transient synced-sentinel, excluded.)
 */
export function isGenuinelyFailed(entry: QueueFate): boolean {
  return entry.status === 'failed' && entry.retries >= entry.maxRetries
}

/** The fields an entry's fate is read from — shared by QueueEntry and its payload-free QueueEntryView. */
type QueueFate = Pick<QueueEntry, 'status' | 'retries' | 'maxRetries' | 'failureReason'>

/**
 * Pinned as a FINAL refusal (online or on replay): the server's answer for this body cannot change,
 * so NO surface may offer "Erneut versuchen" for it — only Discard (queueExit) and contacting the office.
 */
export function isFinalPinned(entry: QueueFate): boolean {
  return isGenuinelyFailed(entry) && entry.failureReason === 'non-retryable-4xx'
}

/**
 * Can a retry plausibly change this entry's fate?
 *
 * FALSE for a deterministic rejection: the server evaluated the body and said no,
 * and it will say no identically on every future replay. Offering "Erneut versuchen"
 * for it is a lie, and ACTING on it (reset → re-pin) is the loop that made the
 * banner immortal. A conflict is likewise not fixed by resending the same body.
 *
 * THE ONE CLASSIFIER, and it must stay one. Both exits consult it: the rep's manual
 * "Erneut versuchen" tap (queueExit.retryFailedEntries) and the automatic on-load
 * recovery (queueRecovery.rePendRecoverableEntries). A second copy of this judgement
 * would let the two paths disagree about what is worth retrying — which is precisely
 * the class of drift that leaves a report stranded. It lives HERE, in LAYER 1, purely
 * so both callers can reach it without an import cycle (it needs nothing but the type).
 */
export function isRetryableReason(reason: QueueFailureReason | undefined): boolean {
  if (reason === 'non-retryable-4xx') return false
  if (reason === 'conflict') return false
  // A dependent pinned behind a dead predecessor is never re-pended on its OWN reason —
  // replaying it alone is the refused, out-of-order write. Both exits move it together
  // with its predecessor instead (queueOrdering.rePendDependentsBehindPendingPredecessors).
  if (reason === 'predecessor-failed') return false
  // 'retries-exhausted' (transient — the network/server may well be back), 'expired',
  // and an untagged legacy entry: a retry is worth offering.
  return true
}

export async function getFailedCount(): Promise<number> {
  const queue = await readQueue()
  return queue.filter(isGenuinelyFailed).length
}

/**
 * The IDs of the genuinely-failed entries.
 *
 * The OfflineIndicator's dismissal used to be keyed on a COUNT held in `useState`,
 * which meant it forgot on every reload AND could not tell "the same stuck report I
 * already acknowledged" from "a new one". Identity, not arithmetic, is what an
 * acknowledgement has to be about (BUG-2026-07-14-VC559).
 */
export async function getFailedEntryIds(): Promise<string[]> {
  const queue = await readQueue()
  return queue.filter(isGenuinelyFailed).map(e => e.id)
}
